/**
 * GraphQL query for fetching section content (highlights and reels).
 * Defines the query structure to retrieve both highlights and reels data for a section,
 * including shows, corners, addons, and IA corners with full pagination support.
 */

import { gql } from '@apollo/client';

/*******************/
/**     QUERY     **/
/*******************/

/**
 * GraphQL sub-query string for fetching section highlights.
 * Retrieves paginated highlight data for the selected section rails.
 *
 * @param query Query params including device identifier, language, and section to load
 * @param params Horizontal pagination parameters
 * @fields To know more, refer to IHighlightData.
 *
 * Highlights query.
 */
const highlights = `highlights(
  query: $query
  params: $highlightsHorizontalParams
) {
    results {
      channelName
      description
      eventId
      id
      image
      imageTV
      isCpg
      order
      sections
      title
      type
    } 
    total
    totalPages
  }`;

/**
 * GraphQL sub-query string for fetching section reels.
 * Retrieves paginated reels data for the selected section rails.
 *
 * @param query Query params including device identifier, language, and section to load
 * @param horizontalParams Horizontal pagination parameters
 * @param verticalParams Vertical pagination parameters
 *
 * Reels query
 */
const reels = `reels(
  query: $query
  horizontalParams: $reelsHorizontalParams
  verticalIndex: $reelVerticalIndex
  verticalParams: $reelsVerticalParams
) {
    results {
      mediaSection {
        actores
        directores
        enabled
        isCpg
        name
        order
        orderField
        position
        presentationType
        sectionTags
        sources
        titles {
          ca
          en
          es
          fr
          de
          it
        }
      }
      shows {
        _id
        ageCode
        beginTime              
        channel
        catchup
        category
        countries
        duration
        endBookmark
        endTime
        episode
        episodeTitle
        eventId
        gender {
          name
          titles {
            en
            es
            ca
          }
        }
        kind
        moviePictures {
          background
          backgroundSrc
          photo
          photoSrc
          poster
          posterSrc
        }
        programId        
        score
        season
        startBookmark
        synopsis
        title
        titleOriginal
        totalEpisode
        type
        url
        year
      }
      corners {
        title
        description
        logoBlanco
        logoColor
        logoGris
        moviePictures {
            photo
            poster
            background
        }
        _id
      }
      addons {
        title
        description
        offerClaim
        controlParental
        plan {
            _id
        }
        logoBlanco
        logoColor
        logoGris
        moviePictures {
            photo
            poster
            background
        }
        _id
      }
      iacorners {
        _id
        code
        description
        name
        title
        moviePictures {
          background
          photo
          poster
          profileCharacterImage
          profileCharacterBackground
        }
      }
      totalItems
      totalPages
    }
    total
    totalPages 
  }`;

/**
 * GraphQL query string for fetching section data.
 *
 * @param query Query params including device identifier, language, and section to load
 * @param hightlightsHorizontalParams Highlights horizontal pagination parameters
 * @param reelsHorizontalParams Reels horizontal pagination parameters
 * @param reelsVerticalParams Reels vertical pagination parameters
 * @returns Section GraphQL query
 */
const query = gql`
  query Section(
    $query: QuerySection!
    $highlightsHorizontalParams: QueryParams!
    $reelVerticalIndex: Int
    $reelsHorizontalParams: QueryParams!
    $reelsVerticalParams: QueryParams!
  ) {
    ${highlights}
    ${reels}
  }
`;

export default query;
